import type { App } from 'vue'
import Logger from '@/utils/Logger'

/**
 * 全局错误处理
 *
 * 三层兜底：
 * 1. app.config.errorHandler —— Vue 组件渲染 / 生命周期 / 侦听器内的同步错误
 * 2. window 'error' —— 组件外的运行时错误、资源加载失败
 * 3. window 'unhandledrejection' —— 未被 catch 的 Promise 拒绝
 *
 * 目标：单点错误不再导致整页白屏，并统一收口到上报入口，便于接入监控（Sentry / 自建）。
 */

interface ErrorInfo {
  type: 'vue' | 'js' | 'promise' | 'resource'
  message: string
  stack?: string
  // 额外上下文，如组件名、生命周期钩子、请求地址等
  extra?: Record<string, unknown>
}

/**
 * 错误上报入口（当前仅打印，后续接入监控平台时在此实现）
 * TODO：接入 Sentry / 自建监控，注意做采样与脱敏，避免上报敏感信息
 */
const reportError = (info: ErrorInfo) => {
  Logger.prettyError('全局异常捕获', `[${info.type}] ${info.message}`)
  if (info.stack) {
    // eslint-disable-next-line no-console
    console.error(info.stack)
  }
  if (info.extra && Object.keys(info.extra).length) {
    // eslint-disable-next-line no-console
    console.error('extra:', info.extra)
  }
}

/** axios 拦截器已统一处理并提示过的错误，这里不重复上报 */
const isHandledRequestError = (err: unknown): boolean => {
  // service.ts 中对业务异常 Promise.reject('error') / reject(string)
  if (typeof err === 'string') return true
  // axios 错误对象带有 isAxiosError 标记，响应拦截器已弹过提示
  return !!(err && typeof err === 'object' && (err as any).isAxiosError)
}

const normalizeError = (err: unknown): { message: string; stack?: string } => {
  if (err instanceof Error) {
    return { message: err.message, stack: err.stack }
  }
  if (typeof err === 'string') {
    return { message: err }
  }
  try {
    return { message: JSON.stringify(err) }
  } catch {
    return { message: String(err) }
  }
}

export const setupErrorHandler = (app: App<Element>) => {
  // 1. Vue 组件内错误
  app.config.errorHandler = (err, instance, info) => {
    const { message, stack } = normalizeError(err)
    reportError({
      type: 'vue',
      message,
      stack,
      extra: {
        // instance?.$options?.name 在 <script setup> 下可能为空，尽力而为
        component: (instance?.$options as any)?.name || instance?.$?.type?.__name || 'unknown',
        // Vue 传入的错误来源描述，如 "render function" / "setup function"
        lifecycle: info
      }
    })
  }

  // 2. 组件外的 JS 运行时错误 & 资源加载失败
  window.addEventListener(
    'error',
    (event: ErrorEvent) => {
      // 资源加载失败（img/script/link 等）没有 error 对象，只有 target
      const target = event.target as HTMLElement | null
      if (target && target !== (window as unknown as HTMLElement) && (target as any).src) {
        reportError({
          type: 'resource',
          message: `资源加载失败：${(target as any).src || (target as any).href}`,
          extra: { tagName: target.tagName }
        })
        return
      }
      const { message, stack } = normalizeError(event.error ?? event.message)
      reportError({ type: 'js', message, stack })
    },
    true // 捕获阶段，才能拿到资源加载错误
  )

  // 3. 未处理的 Promise 拒绝
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isHandledRequestError(event.reason)) {
      // 请求类错误已由 axios 拦截器处理，阻止控制台重复报红即可
      event.preventDefault()
      return
    }
    const { message, stack } = normalizeError(event.reason)
    reportError({ type: 'promise', message, stack })
  })
}
